import { ChangeEvent, useMemo, useState } from "react";
import { ApiError } from "../../api/client";
import {
  ImportEndpoint,
  ImportRowReport,
  ImportSummary,
  downloadImportTemplate,
  uploadImportXlsx,
} from "../../api/imports";
import { UI_LOADING } from "../../constants/uiLabels";
import "./ExcelImportModal.css";

type Props = {
  title: string;                  // ornek: "Eğitmen Toplu Yükleme (Excel)"
  description?: string;           // 1. adim altinda gosterilecek ipucu
  endpoint: ImportEndpoint;
  branchId?: string;
  onClose: () => void;
  onImported: (summary: ImportSummary) => void;
  successLabel?: (count: number) => string; // ornek: (n) => `${n} egitmen eklendi.`
};

const ALLOWED_EXT = [".xlsx"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function ExcelImportModal({
  title,
  description,
  endpoint,
  branchId,
  onClose,
  onImported,
  successLabel,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<"idle" | "validating" | "importing">("idle");

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    setSummary(null);
    setError(null);
    if (!next) {
      setFile(null);
      return;
    }
    const lower = next.name.toLowerCase();
    if (!ALLOWED_EXT.some((ext) => lower.endsWith(ext))) {
      setFile(null);
      setError("Sadece .xlsx dosyalari kabul edilir.");
      return;
    }
    if (next.size === 0) {
      setFile(null);
      setError("Dosya bos.");
      return;
    }
    if (next.size > MAX_BYTES) {
      setFile(null);
      setError("Dosya 5MB sinirini asiyor.");
      return;
    }
    setFile(next);
  };

  const handleTemplate = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadImportTemplate(endpoint);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Şablon indirilemedi.");
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setPhase(dryRun ? "validating" : "importing");
    try {
      const result = await uploadImportXlsx(endpoint, file, { dryRun, branchId });
      setSummary(result);
      if (!dryRun && result.inserted_count > 0) {
        onImported(result);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "İçe Aktarma Başarısız.");
    } finally {
      setBusy(false);
      setPhase("idle");
    }
  };

  const errorRows = useMemo<Extract<ImportRowReport, { status: "error" }>[]>(
    () =>
      summary
        ? (summary.rows.filter(
            (r): r is Extract<ImportRowReport, { status: "error" }> => r.status === "error",
          ))
        : [],
    [summary],
  );

  const canImport =
    !!file &&
    !!summary &&
    summary.error_count === 0 &&
    summary.valid_count > 0 &&
    summary.dry_run;

  return (
    <div
      className="import-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-modal-title"
    >
      <div className="import-modal">
        <div className="import-modal__header">
          <h3 id="import-modal-title">{title}</h3>
          <button
            type="button"
            className="import-modal__close"
            onClick={onClose}
            aria-label="Kapat"
          >
            x
          </button>
        </div>

        <div className="import-modal__body">
          <section className="import-modal__step">
            <h4>1. Şablonu Indir</h4>
            <p className="import-modal__hint">
              {description ||
                "Tum sutunlarin Turkce basliklari ve ornek bir Satır icerir. Zorunlu alanlar isaretlidir."}
            </p>
            <button
              type="button"
              className="import-modal__btn-secondary"
              onClick={handleTemplate}
              disabled={busy}
            >
              Excel Şablonunu Indir
            </button>
          </section>

          <section className="import-modal__step">
            <h4>2. Doldurup Yukle</h4>
            <input type="file" accept=".xlsx" onChange={onFileChange} disabled={busy} />
            {file && (
              <div className="import-modal__file-info">
                Secilen: {file.name} ({Math.round(file.size / 1024)} KB)
              </div>
            )}
          </section>

          <section className="import-modal__step">
            <h4>3. Önizleme & Yükleme</h4>
            <div className="import-modal__actions-row">
              <button
                type="button"
                className="import-modal__btn-secondary"
                onClick={() => runImport(true)}
                disabled={!file || busy}
              >
                {phase === "validating" ? "Kontrol ediliyor..." : "Önizleme (kuru calistir)"}
              </button>
              <button
                type="button"
                className="import-modal__btn-primary"
                onClick={() => runImport(false)}
                disabled={!canImport || busy}
                title={canImport ? "" : "Önce 'Önizleme'de Hatasız Doğrulayın"}
              >
                {phase === "importing" ? UI_LOADING : "Yukle"}
              </button>
            </div>
          </section>

          {error && <div className="import-modal__error">{error}</div>}

          {summary && (
            <section className="import-modal__summary">
              <div className="import-modal__summary-row">
                <span className="badge badge-info">Toplam: {summary.total_rows}</span>
                <span className="badge badge-ok">Gecerli: {summary.valid_count}</span>
                <span className="badge badge-err">Hata: {summary.error_count}</span>
                {summary.dry_run ? (
                  <span className="badge badge-warn">Önizleme</span>
                ) : (
                  <span className="badge badge-ok">
                    Eklenen: {summary.inserted_count}
                  </span>
                )}
              </div>

              {summary.dry_run && summary.error_count === 0 && summary.valid_count > 0 && (
                <div className="import-modal__success-hint">
                  Tum Satırlar gecerli. "Yukle" dugmesi ile veritabanina ekleyebilirsiniz.
                </div>
              )}

              {!summary.dry_run && summary.inserted_count > 0 && (
                <div className="import-modal__success-hint">
                  {successLabel
                    ? successLabel(summary.inserted_count)
                    : `${summary.inserted_count} kayit Başarıyla eklendi.`}
                </div>
              )}

              {errorRows.length > 0 && (
                <div className="import-modal__errors-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Satir</th>
                        <th>Hata</th>
                        <th>Alan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errorRows.map((row) => (
                        <tr key={row.row_number}>
                          <td>{row.row_number}</td>
                          <td>{row.error.message}</td>
                          <td className="muted">{row.error.field || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="import-modal__footer">
          <button
            type="button"
            className="import-modal__btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}
